import React, { useRef, useState } from 'react';
import { Modal, Button, message, Spin } from 'antd';
import { PrinterOutlined, DownloadOutlined } from '@ant-design/icons';
import { PDFDocument } from 'pdf-lib';
import request from '../../../utils/request';

export interface LabelPreviewState {
  /** 本地 blob URL（openLabel/mergeAndPreview 拉取内容后生成），不是后端原始地址。 */
  url: string;
  /** 下载文件名；单个标签用跟踪号+真实 Content-Type 推出来的后缀，合并标签固定传。 */
  downloadFilename?: string;
  /** 能不能直接在 iframe 里预览/打印——按响应真实的 Content-Type 判断，PDF/常见图片
   * 格式可以直接预览，像 ZPL 这种本来就不是给人眼睛看的格式，硬塞进 iframe 只会是
   * 一片空白，不如直接提示下载，用专门的标签打印软件打开。 */
  previewable: boolean;
}

interface LabelLike {
  label_url?: string | null;
}

const PREVIEWABLE_CONTENT_TYPES = ['application/pdf', 'image/'];
const CONTENT_TYPE_TO_EXT: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
};

// label_url 现在指向的是动态处理端点（如 /shipment-records/48/label/），URL 本身不带
// 文件后缀了，判断"能不能直接预览"只能靠响应真实的 Content-Type，不能再从 URL 猜。
const extFromContentType = (contentType: string): string => CONTENT_TYPE_TO_EXT[contentType] || '';

const isPreviewableContentType = (contentType: string): boolean =>
  PREVIEWABLE_CONTENT_TYPES.some((prefix) => contentType.startsWith(prefix));

/**
 * 运单标签的查看/打印/下载/多标签合并预览——PurchaseModal（购买成功弹窗）和物流记录列表页
 * 共用同一套逻辑，不重复实现。用法：
 *   const { openLabel, mergeAndPreview, modal } = useLabelPreview();
 *   ... <Button onClick={() => openLabel(item.label_url, item.tracking_number)}>查看/打印运单</Button>
 *   ... 在组件最外层渲染一次 {modal}
 */
export function useLabelPreview() {
  const [preview, setPreview] = useState<LabelPreviewState | null>(null);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // label_url 指向的是需要登录态的后端接口（JWT 走 Authorization 头，不是 Cookie），
  // 不能直接当 <iframe src>/<a href> 用——浏览器发起的这类"裸"跨源请求不会带上
  // localStorage 里的 token，只有账号恰好还留着 Django 后台的登录 Cookie 时才会
  // 侥幸能访问（这也是之前管理员账号能看、客户账号显示"localhost 已拒绝连接"的
  // 原因）。统一改成用带鉴权头的 request 实例把内容拉下来，转成本地 blob 再预览，
  // 对所有账号类型（管理员/员工/客户）行为一致。
  const openLabel = async (labelUrl: string, trackingNumber?: string | null) => {
    setLoading(true);
    try {
      const resp = await request.get<Blob>(labelUrl, { responseType: 'blob' });
      const contentType = String(resp.headers?.['content-type'] || '').split(';')[0].trim();
      const ext = extFromContentType(contentType) || 'pdf';
      const blobUrl = URL.createObjectURL(resp.data);
      setPreview({
        url: blobUrl,
        downloadFilename: trackingNumber ? `${trackingNumber}.${ext}` : undefined,
        previewable: isPreviewableContentType(contentType),
      });
    } catch (error) {
      console.error('加载运单标签失败:', error);
      message.error('加载运单标签失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const closePreview = () => {
    // blob URL 用完要主动释放，不然占着内存不放
    if (preview?.url.startsWith('blob:')) {
      URL.revokeObjectURL(preview.url);
    }
    setPreview(null);
  };

  // 把多个运单各自独立的标签 PDF，按传入顺序拼接成一份 PDF 预览——每个标签本身就是独立文件，
  // 客户/仓库更习惯拿到一份连续的文件直接批量打印，不用一个一个点开。纯前端合并（pdf-lib），
  // 不需要后端额外提供接口。mergedFilenamePrefix 通常传承运商代码（如 "USPS"），
  // 不传中文名——合并后下载的文件名是 "{prefix}×{数量}.pdf"。
  const mergeAndPreview = async (items: LabelLike[], mergedFilenamePrefix: string) => {
    const successItems = items.filter((i) => i.label_url);
    if (successItems.length < 2) {
      message.error('没有足够可以合并的运单标签');
      return;
    }
    setMerging(true);
    try {
      const merged = await PDFDocument.create();
      for (const item of successItems) {
        // 跟 openLabel 一样，必须带鉴权头拉取，不能用裸 fetch（客户账号会因为没有
        // Django 后台的登录 Cookie 而请求失败）。
        const resp = await request.get<ArrayBuffer>(item.label_url as string, { responseType: 'arraybuffer' });
        const src = await PDFDocument.load(resp.data);
        const pages = await merged.copyPages(src, src.getPageIndices());
        pages.forEach((page) => merged.addPage(page));
      }
      const mergedBytes = await merged.save();
      const blob = new Blob([mergedBytes as BlobPart], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      // 合并出来的永远是真实拼好的 PDF（pdf-lib 直接操作 PDF 字节），不用像单个标签那样
      // 去猜格式，一定可以预览。
      setPreview({ url: blobUrl, downloadFilename: `${mergedFilenamePrefix}×${successItems.length}.pdf`, previewable: true });
    } catch (error) {
      console.error('合并运单标签失败:', error);
      message.error('合并运单标签失败，请稍后重试，或单独查看/打印每个标签');
    } finally {
      setMerging(false);
    }
  };

  const handlePrint = () => {
    const win = iframeRef.current?.contentWindow;
    if (!win) {
      message.error('标签尚未加载完成，请稍候再试');
      return;
    }
    win.focus();
    win.print();
  };

  const handleDownload = () => {
    if (!preview) return;
    // 单个标签和合并后的标签现在都是本地生成的 blob URL（同源），<a download> 直接生效，
    // 不用再区分"跨域要先 fetch 转 blob"这两条路径。
    const filename = preview.downloadFilename || 'label.pdf';
    const a = document.createElement('a');
    a.href = preview.url;
    a.download = filename;
    a.click();
  };

  const modal = (
    <Modal
      title="运单标签预览"
      open={loading || !!preview}
      onCancel={closePreview}
      width={700}
      destroyOnClose
      footer={loading ? null : [
        <Button key="download" icon={<DownloadOutlined />} onClick={handleDownload}>
          下载
        </Button>,
        <Button
          key="print"
          type="primary"
          icon={<PrinterOutlined />}
          onClick={handlePrint}
          disabled={!preview?.previewable}
        >
          打印
        </Button>,
        <Button key="close" onClick={closePreview}>
          关闭
        </Button>,
      ]}
    >
      {loading ? (
        <div style={{ padding: '100px 24px', textAlign: 'center' }}>
          <Spin size="large" tip="标签加载中…" />
        </div>
      ) : preview && (
        preview.previewable ? (
          <iframe
            ref={iframeRef}
            src={preview.url}
            title="运单标签"
            style={{ width: '100%', height: '75vh', border: 'none' }}
          />
        ) : (
          <div style={{ padding: '80px 24px', textAlign: 'center', color: '#999' }}>
            该运单标签不是可直接预览的格式，请点击下方"下载"按钮获取文件，用专门的标签打印软件打开。
          </div>
        )
      )}
    </Modal>
  );

  return { preview, merging, loading, openLabel, closePreview, mergeAndPreview, modal };
}
